class PincodeChecker extends HTMLElement {
  constructor() {
    super();
    this.inMemoryData = null;
  }

  connectedCallback() {
    // Defer initialization to ensure children are fully parsed by the browser
    setTimeout(() => {
      this.button = this.querySelector('[data-pincode-btn]');
      this.input = this.querySelector('[data-pincode-input]');
      this.failureNode = this.querySelector('[data-pincode-failure]');
      this.successNode = this.querySelector('[data-pincode-success]');
      this.errorNode = this.querySelector('[data-pincode-error]');
      this.cityNode = this.querySelector('[data-pincode-city]');
      this.resultNode = this.querySelector('[data-pincode-result]');

      // Read config from data attributes (set via Shopify Theme Editor)
      this.spreadsheetId = this.dataset.spreadsheetId || '';
      this.apiKey = this.dataset.apiKey || '';
      this.sheetName = this.dataset.sheetName || 'Sheet1';

      // Event listeners
      if (this.button) {
        this.button.addEventListener('click', () => this.checkPincodeAvailability());
      }
      
      if (this.input) {
        this.input.addEventListener('keypress', (e) => {
          if (e.key === 'Enter') this.checkPincodeAvailability();
        });

        // Only allow numeric input
        this.input.addEventListener('input', () => {
          this.input.value = this.input.value.replace(/\D/g, '');
        });
      }

      // Restore saved pincode from localStorage
      this.reuseLocalStorageData();
    }, 0);
  }

  async checkPincodeAvailability() {
    if (!this.input || !this.button) return;
    
    const pincode = this.input.value.trim();
    this.hideAllResults();

    // Validate 6-digit numeric pincode
    if (!pincode || pincode.length !== 6 || isNaN(pincode)) {
      this.showValidationError('Please enter a valid 6-digit pincode');
      return;
    }

    // Show loading state
    this.button.classList.add('loading');
    this.button.disabled = true;

    try {
      const result = await this.findPincode(pincode);

      if (result.error) {
        // Network or fetch error occurred
        this.showValidationError(result.message || 'Unable to check pincode. Please try again.');
      } else if (result.success) {
        // Pincode found → delivery available
        if (this.successNode) this.successNode.classList.remove('ep-hidden');

        if (result.city && this.cityNode) {
          this.cityNode.textContent = 'Delivery available to ' + result.city.charAt(0).toUpperCase() + result.city.slice(1).toLowerCase();
          this.cityNode.classList.remove('ep-hidden');
        }

        if (this.resultNode) this.resultNode.classList.remove('ep-hidden');
        this.safeSetLocalStorage('pincode', pincode);
      } else {
        // Pincode confirmed not found in sheet → not serviceable
        if (this.failureNode) this.failureNode.classList.remove('ep-hidden');
        if (this.resultNode) this.resultNode.classList.remove('ep-hidden');
      }
    } catch (error) {
      console.error('Pincode checker error:', error);
      this.showValidationError('Something went wrong. Please try again.');
    } finally {
      if (this.button) {
        this.button.classList.remove('loading');
        this.button.disabled = false;
      }
    }
  }

  /**
   * Look up pincode in the cached/fetched Google Sheet data.
   * Columns expected: Delivery City (A), Delivery Pincode (B), Courier (C), Zone (D)
   */
  async findPincode(pincode) {
    let dataRes;
    try {
      dataRes = await this.fetchSheetsData();
    } catch (err) {
      return { success: false, error: true, message: 'Failed to fetch delivery data. Please try again.' };
    }

    if (!dataRes || !dataRes.values || dataRes.values.length < 2) {
      return { success: false, error: true, message: 'Delivery data is unavailable. Please try again.' };
    }

    const [header, ...rows] = dataRes.values;

    // Find column indices dynamically from header row
    const pincodeColIndex = header.findIndex(
      (h) => h && (
        h.toLowerCase().trim() === 'delivery pincode' ||
        h.toLowerCase().trim() === 'pincode' ||
        h.toLowerCase().trim() === 'pin code' ||
        h.toLowerCase().trim() === 'zipcode'
      )
    );
    const cityColIndex = header.findIndex(
      (h) => h && (
        h.toLowerCase().trim() === 'delivery city' ||
        h.toLowerCase().trim() === 'city' ||
        h.toLowerCase().trim() === 'location'
      )
    );

    if (pincodeColIndex === -1) {
      console.error('PincodeChecker: Pincode column not found in sheet header:', header);
      return { success: false, error: true, message: 'Configuration error: Pincode column not found.' };
    }

    // Find first matching row (resilient against whitespace, string/number formats)
    const match = rows.find((row) => {
      if (!row[pincodeColIndex]) return false;
      const str = String(row[pincodeColIndex]).trim();
      return str === pincode || str.replace(/\D/g, '') === pincode;
    });

    if (match) {
      return {
        success: true,
        pincode: pincode,
        city: cityColIndex !== -1 ? String(match[cityColIndex] || '').trim() : '',
      };
    }

    return { success: false, notFound: true };
  }

  /**
   * Fetch sheet data from Google Sheets API with in-memory & safe localStorage cache.
   */
  async fetchSheetsData() {
    if (!this.spreadsheetId || !this.apiKey) {
      throw new Error('Spreadsheet ID or API Key missing');
    }

    // 1. Check in-memory cache first
    if (this.inMemoryData && this.inMemoryData.values) {
      return this.inMemoryData;
    }

    const cacheKey = 'pincodeSheetData_' + this.spreadsheetId;
    const cacheTimeKey = cacheKey + '_time';

    // 2. Check localStorage cache (1 hour TTL)
    const cached = this.safeGetLocalStorage(cacheKey);
    const cacheTime = this.safeGetLocalStorage(cacheTimeKey);

    if (cached && cacheTime && Date.now() - parseInt(cacheTime, 10) < 3600000) {
      try {
        const parsed = JSON.parse(cached);
        if (parsed && parsed.values) {
          this.inMemoryData = parsed;
          return parsed;
        }
      } catch (e) {
        console.warn('PincodeChecker: Corrupted cache in localStorage', e);
      }
    }

    // 3. Fetch fresh data from Google Sheets API
    const BASE_URL =
      'https://sheets.googleapis.com/v4/spreadsheets/' +
      this.spreadsheetId +
      '/values/' +
      encodeURIComponent(this.sheetName);
    const queryString =
      '?key=' +
      this.apiKey +
      '&majorDimension=ROWS&valueRenderOption=FORMATTED_VALUE&dateTimeRenderOption=FORMATTED_STRING';

    try {
      const response = await fetch(BASE_URL + queryString);

      if (!response.ok) {
        throw new Error('Google Sheets API returned status ' + response.status);
      }

      const data = await response.json();
      this.inMemoryData = data;

      // Safely try caching in localStorage without failing if quota is exceeded
      this.safeSetLocalStorage(cacheKey, JSON.stringify(data));
      this.safeSetLocalStorage(cacheTimeKey, String(Date.now()));

      return data;
    } catch (error) {
      console.error('PincodeChecker: Failed to fetch sheet data:', error);

      // Fall back to stale localStorage cache if available
      if (cached) {
        try {
          const parsed = JSON.parse(cached);
          if (parsed && parsed.values) {
            this.inMemoryData = parsed;
            return parsed;
          }
        } catch (e) {}
      }

      throw error;
    }
  }

  /**
   * Safe wrapper for localStorage.getItem to prevent exceptions in private browsing or restricted environments.
   */
  safeGetLocalStorage(key) {
    try {
      return localStorage.getItem(key);
    } catch (e) {
      console.warn('PincodeChecker: localStorage read failed:', e);
      return null;
    }
  }

  /**
   * Safe wrapper for localStorage.setItem to prevent QuotaExceededError or security exceptions from crashing execution.
   */
  safeSetLocalStorage(key, value) {
    try {
      localStorage.setItem(key, value);
    } catch (e) {
      console.warn('PincodeChecker: localStorage write failed (quota exceeded or blocked):', e);
    }
  }

  /**
   * Hide all result messages.
   */
  hideAllResults() {
    if (this.successNode) this.successNode.classList.add('ep-hidden');
    if (this.failureNode) this.failureNode.classList.add('ep-hidden');
    if (this.errorNode) this.errorNode.classList.add('ep-hidden');
    if (this.cityNode) this.cityNode.classList.add('ep-hidden');
    if (this.resultNode) this.resultNode.classList.add('ep-hidden');
  }

  /**
   * Show a validation / generic error message (amber).
   */
  showValidationError(message) {
    if (!this.errorNode || !this.resultNode) return;
    const span = this.errorNode.querySelector('span');
    if (span) span.textContent = message;
    
    this.errorNode.classList.remove('ep-hidden');
    this.resultNode.classList.remove('ep-hidden');
  }

  /**
   * Restore a previously-checked pincode from localStorage and auto-check.
   */
  reuseLocalStorageData() {
    if (!this.input) return;
    const savedPincode = this.safeGetLocalStorage('pincode');
    if (savedPincode) {
      this.input.value = savedPincode;
      this.checkPincodeAvailability();
    }
  }
}

if (!customElements.get('pincode-checker')) {
  customElements.define('pincode-checker', PincodeChecker);
}
